import { Link, useParams, useNavigate } from 'react-router-dom';

import { useAgent } from '@/hooks/useAgents';
import { useAgentSessions } from '@/hooks/useSessions';
import { AgentBadge, SessionBadge, VerdictBadge } from '@/components/common/StatusBadge';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { isAgentOnline, relTime, shortId, fmtDuration, fmtMbps } from '@/lib/format';

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="spread" style={{ padding: '9px 0', borderBottom: '1px solid var(--line)' }}>
      <span className="label" style={{ letterSpacing: '0.1em' }}>{label}</span>
      <span className="mono dim" style={{ fontSize: 13 }}>{value}</span>
    </div>
  );
}

export function AgentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: a, isLoading, error } = useAgent(id);
  const sessions = useAgentSessions(id);

  if (isLoading) return <Loading label="Loading agent" />;
  if (error) return <ErrorBanner error={error} />;
  if (!a) return <EmptyState title="Agent not found" />;

  const online = isAgentOnline(a.last_seen_at);

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="row" style={{ gap: 10 }}>
          <Link to="/agents" className="btn btn-ghost" style={{ padding: '4px 8px' }}>←</Link>
          <div className="eyebrow">agent</div>
        </div>
        <div className="title" style={{ marginTop: 6 }}>{a.label || shortId(a.external_id)}</div>
        <div className="row wrap" style={{ gap: 10, marginTop: 8 }}>
          <AgentBadge online={online} />
          {a.platform && <span className="mono dim" style={{ fontSize: 12 }}>{a.platform}</span>}
          {a.agent_version && <span className="mono dim" style={{ fontSize: 12 }}>v{a.agent_version}</span>}
          {a.last_ip && <span className="mono dim" style={{ fontSize: 12 }}>{a.last_ip}</span>}
        </div>
      </div>

      <div className="card" style={{ maxWidth: 560, marginBottom: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>identity</div>
        <Meta label="ID" value={a.id} />
        <Meta label="External ID" value={a.external_id} />
        <Meta label="Last IP" value={a.last_ip ?? '—'} />
        <Meta label="Platform" value={a.platform ?? '—'} />
        <Meta label="Agent version" value={a.agent_version ?? '—'} />
        <Meta label="Capabilities" value={a.capabilities.length ? a.capabilities.join(', ') : '—'} />
        <Meta label="PSK kid" value={a.psk_kid} />
        <Meta label="Last seen" value={relTime(a.last_seen_at)} />
        <div className="spread" style={{ padding: '9px 0' }}>
          <span className="label" style={{ letterSpacing: '0.1em' }}>Enrolled</span>
          <span className="mono dim" style={{ fontSize: 13 }}>{relTime(a.created_at)}</span>
        </div>
      </div>

      <div className="panel">
        <div style={{ padding: '14px 16px 6px' }}>
          <div className="eyebrow">sessions · this agent</div>
        </div>
        {sessions.isLoading ? (
          <Loading label="Loading sessions" />
        ) : !sessions.data || sessions.data.length === 0 ? (
          <div className="dim mono" style={{ fontSize: 13, padding: '18px 16px' }}>
            No sessions yet for this agent.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Session</th>
                <th>Status</th>
                <th>Verdict</th>
                <th className="right">Peak RX</th>
                <th className="right">Duration</th>
                <th className="right">Started</th>
              </tr>
            </thead>
            <tbody>
              {sessions.data.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/sessions/${s.id}`} className="mono" style={{ color: 'var(--signal)' }}>
                      {s.label || shortId(s.id)}
                    </Link>
                  </td>
                  <td><SessionBadge status={s.status} /></td>
                  <td><VerdictBadge verdict={s.verdict} /></td>
                  <td className="right mono dim">{fmtMbps(s.summary_json?.rx_max_mbps ?? null)}</td>
                  <td className="right mono dim">{fmtDuration(s.duration_seconds)}</td>
                  <td className="right dim">{s.started_at ? relTime(s.started_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <button className="btn btn-ghost" onClick={() => nav('/agents')}>← Back to agents</button>
      </div>
    </div>
  );
}
